import { SettingsButton } from '@/components/auth/settings-button';
import { ThemeToggle } from '@/components/auth/theme-toggle';
import { Megaphone, Plus, Sparkles } from 'lucide-react';
import { AssistantPanel } from '@/features/assistant/assistant-panel';
import { CreateAbsenceRequestDialog } from '@/features/absences/components/create-absence-request-dialog';
import { useCreateAbsenceRequestDialog } from '@/features/absences/context/create-absence-request-dialog-context';
import { NotificationBell } from '@/features/notifications/notification-bell';
import { GlobalTimerWidget } from '@/features/time-tracker/components/global-timer-widget';
import { useExtensionsStore } from '@/stores/extensions';
import { useTimerStore } from '@/stores/timer';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

export function GlobalHeader() {
  const navigate = useNavigate();
  const { open: createDialogOpen, setOpen: setCreateDialogOpen, openDialog } =
    useCreateAbsenceRequestDialog();
  const isTimeTrackerEnabled = useExtensionsStore((s) => s.isExtensionEnabled('time-tracker'));
  const isAssistantEnabled = useExtensionsStore((s) => s.isExtensionEnabled('ai-assistant'));
  const timerStore = useTimerStore();
  const [assistantOpen, setAssistantOpen] = useState(false);
  const onAiView = useRouterState({ select: (st) => st.location.pathname.startsWith('/ai') });

  useEffect(() => {
    if (isTimeTrackerEnabled) {
      timerStore.load();
    }
  }, [isTimeTrackerEnabled]);

  // ⌘K / Ctrl+K opens the assistant from anywhere. Ignored while typing so it
  // never steals the shortcut from a text field — and not bound at all while
  // the assistant extension is off, so the browser keeps its own ⌘K.
  useEffect(() => {
    if (!isAssistantEnabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'k' || !(e.metaKey || e.ctrlKey)) return;
      const el = e.target as HTMLElement | null;
      if (el?.isContentEditable || ['INPUT', 'TEXTAREA'].includes(el?.tagName ?? '')) return;
      e.preventDefault();
      setAssistantOpen((prev) => !prev);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isAssistantEnabled]);

  return (
    <TooltipProvider delayDuration={0}>
      <header className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-border bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60 px-6">
        <div className="flex items-center gap-4">
          {/* The two ways to drive the same product. A segmented control rather
              than another icon: the point is that AI is a mode of HyggeOS, not a
              utility parked next to it — so when the assistant extension is off
              the control goes entirely, rather than leaving a dead half. */}
          {isAssistantEnabled && (
          <div
            role="group"
            aria-label="Workspace mode"
            className="mr-1 hidden items-center rounded-md border border-border p-0.5 sm:flex"
          >
            <Link
              to="/people"
              aria-current={onAiView ? undefined : 'page'}
              className={cn(
                'rounded-[5px] px-2.5 py-1 text-xs transition-colors',
                onAiView ? 'text-muted-foreground hover:text-foreground' : 'bg-muted font-medium text-foreground',
              )}
            >
              General
            </Link>
            <Link
              to="/ai"
              aria-current={onAiView ? 'page' : undefined}
              className={cn(
                'flex items-center gap-1 rounded-[5px] px-2.5 py-1 text-xs transition-colors',
                onAiView ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Sparkles className="h-3 w-3" />
              AI
            </Link>
          </div>
          )}
          {isTimeTrackerEnabled && <GlobalTimerWidget />}
        </div>
        <div className="flex items-center gap-2">
          {/* New Request Button - Icon style with Request Absence background */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={openDialog}
                className={cn(
                  'flex h-9 w-9 items-center justify-center relative',
                  'rounded-full',
                  // The brand's primary action: mint fill with its own ink, in
                  // both themes. [HW-926]
                  'bg-cta',
                  'shadow-md shadow-cta/20',
                  'transition-all duration-200',
                  'hover:bg-cta/90',
                  'hover:shadow-lg hover:shadow-cta/30',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                  'cursor-pointer',
                  'group'
                )}
                aria-label="Create new absence request"
              >
                <Plus className="h-4 w-4 text-cta-foreground transition-transform group-hover:rotate-90" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>New Absence Request</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => navigate({ to: '/settings/changelog' })}
                className={cn(
                  'flex h-9 w-9 items-center justify-center',
                  'rounded-full bg-hygge/10',
                  'transition-colors',
                  'hover:bg-hygge/20',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  'cursor-pointer',
                )}
                aria-label="What's New"
              >
                <Megaphone className="h-4 w-4 text-hygge" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>What's New</p>
            </TooltipContent>
          </Tooltip>
          {isAssistantEnabled && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setAssistantOpen(true)}
                className={cn(
                  'flex h-9 w-9 items-center justify-center',
                  'rounded-full bg-hygge/10',
                  'transition-colors',
                  'hover:bg-hygge/20',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  'cursor-pointer',
                )}
                aria-label="Open assistant"
              >
                <Sparkles className="h-4 w-4 text-hygge" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>Assistant (⌘K)</p>
            </TooltipContent>
          </Tooltip>
          )}
          <NotificationBell />
          <SettingsButton />
          <ThemeToggle />
        </div>
      </header>

      {isAssistantEnabled && (
        <AssistantPanel open={assistantOpen} onOpenChange={setAssistantOpen} />
      )}

      {/* Global Create Absence Request Dialog */}
      <CreateAbsenceRequestDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />
    </TooltipProvider>
  );
}
